import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ExperimentalFeatureToggles from "./ExperimentalFeatureToggles";
import { useExperimental } from "../context/ExperimentalFeaturesContext";
import {
  enterTestSession, exitTestSession, fetchTestAccounts, readCachedTestAccounts,
} from "../utils/testSession";
import type { TestEnvironmentSummary, TestEnvironmentUser } from "../hooks/usePlatformAdmin";
import type { User } from "../types";

interface TestSessionBannerProps {
  currentUser: User;
  agencyName?: string | null;
  onLogout: () => void | Promise<void>;
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Agency admin",
  secretary: "Secretary",
  officer: "Officer",
  entity: "Organization",
};

/**
 * Persistent switcher shown while a platform admin operates as a sandbox
 * user. Makes it unmistakable that everything on screen is sample data,
 * lets the admin hop straight to any other test account, and offers a
 * one-click way back to the platform panel — no signing in or out.
 */
export default function TestSessionBanner({ currentUser, agencyName, onLogout }: TestSessionBannerProps) {
  const navigate = useNavigate();
  const experimental = useExperimental();
  const [accounts, setAccounts] = useState<TestEnvironmentSummary | null>(() => readCachedTestAccounts());
  const [switching, setSwitching] = useState<"exit" | string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const featuresRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!featuresOpen) return;
    const onDown = (e: MouseEvent) => {
      if (featuresRef.current && !featuresRef.current.contains(e.target as Node)) setFeaturesOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFeaturesOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [featuresOpen]);

  useEffect(() => {
    let cancelled = false;
    fetchTestAccounts()
      .then((data) => { if (!cancelled) setAccounts(data); })
      .catch(() => { /* keep the cached list; the dropdown just won't refresh */ });
    return () => { cancelled = true; };
  }, []);

  const groups = useMemo(() => {
    if (!accounts) return [];
    const enterable = (users: TestEnvironmentUser[]) => users.filter((u) => u.canEnter);
    return [
      ...accounts.agencies.map((a) => ({ id: a.id, label: a.name, users: enterable(a.users) })),
      ...accounts.entityOrgs.map((o) => ({ id: o.id, label: o.name, users: enterable(o.users) })),
    ].filter((g) => g.users.length > 0);
  }, [accounts]);

  const switchTo = async (userId: string) => {
    if (!userId || userId === currentUser.id) return;
    setSwitching(userId);
    setError(null);
    try {
      await enterTestSession(userId);
      // The auth listener swaps the whole app to the new account; land on
      // its dashboard rather than a route that may not exist for that role.
      navigate("/", { replace: true });
    } catch (err) {
      setError((err as Error).message || "Couldn't switch accounts.");
      setSwitching(null);
    }
  };

  const handleExit = async () => {
    setSwitching("exit");
    setError(null);
    try {
      await exitTestSession();
      navigate("/test-environment", { replace: true });
    } catch (err) {
      setError((err as Error).message || "Couldn't return to the platform panel.");
      setSwitching(null);
    }
  };

  const who = currentUser.name || currentUser.email;
  const where = agencyName || (currentUser.role === "entity" ? "organization portal" : null);
  const busy = switching !== null;

  return (
    <div className="test-session-banner" role="status" data-testid="test-session-banner">
      <div className="test-session-banner-left">
        <span className="test-session-banner-tag">{"🧪"} TEST ENVIRONMENT</span>
        <span className="test-session-banner-detail" title="Everything on screen is sample data">
          Operating as <strong>{who}</strong>
          <span className="test-session-banner-sep">·</span>{ROLE_LABELS[currentUser.role] || currentUser.role}
          {where && <><span className="test-session-banner-sep">·</span>{where}</>}
        </span>
      </div>
      <div className="test-session-banner-right">
        <label className="test-session-banner-switch">
          <span className="test-session-banner-detail">Switch to</span>
          <select
            aria-label="Switch test account"
            value={currentUser.id}
            disabled={busy || groups.length === 0}
            onChange={(e) => switchTo(e.target.value)}
          >
            {groups.length === 0 && <option value={currentUser.id}>{who}</option>}
            {groups.map((group) => (
              <optgroup key={group.id} label={group.label}>
                {group.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} — {ROLE_LABELS[u.role] || u.role}
                  </option>
                ))}
              </optgroup>
            ))}
            {groups.length > 0 && !groups.some((g) => g.users.some((u) => u.id === currentUser.id)) && (
              <option value={currentUser.id}>{who}</option>
            )}
          </select>
        </label>
        {busy && switching !== "exit" && <span className="test-session-banner-detail">Switching…</span>}
        <div className="test-session-features" ref={featuresRef}>
          <button
            type="button"
            className={`test-session-banner-btn test-session-banner-btn-secondary ${experimental.enabledCount > 0 ? "test-session-banner-btn-active" : ""}`}
            aria-haspopup="dialog"
            aria-expanded={featuresOpen}
            onClick={() => setFeaturesOpen((open) => !open)}
          >
            {"🔬"} Features{experimental.enabledCount > 0 ? ` (${experimental.enabledCount} on)` : ""}
          </button>
          {featuresOpen && (
            <div className="test-session-features-popover" role="dialog" aria-label="Experimental features">
              <div className="test-session-features-title">Experimental features</div>
              <p className="test-session-features-hint">Switches apply instantly to this test session only.</p>
              <ExperimentalFeatureToggles compact />
            </div>
          )}
        </div>
        <button type="button" className="test-session-banner-btn" onClick={handleExit} disabled={busy}>
          {switching === "exit" ? "Returning…" : "Back to Platform Admin"}
        </button>
      </div>
      {error && (
        <div className="test-session-banner-error" role="alert">
          {error}{" "}
          <button type="button" className="test-session-banner-btn" onClick={onLogout} style={{ marginLeft: 6 }}>
            Sign out instead
          </button>
        </div>
      )}
    </div>
  );
}
